// Sale simplified tax on residential property sold by an individual.
//
// Tax Code Art. 220.8: 15 AZN per m², multiplied by the property's
// location-zone coefficient. For residential property the first 30 m²
// is exempt, and if the owner was registered at the address for at
// least 3 years the sale is fully exempt.
//
// Zone boundaries + coefficients come from Cabinet of Ministers
// Decision No. 412 (30 Dec 2015), e-qanun.az/framework/31796 - the
// same polygons rendered by the tax-zones layer on /map. This is the
// SALE simplified tax, distinct from the annual property tax (which
// shares the same zone map but uses 0.1-0.4 AZN/m² and 0.7-1.5
// coefficients).

import { HEATMAP_COLORS } from "@/lib/heatmap-colors";

export const SALE_TAX_RATE_PER_M2 = 15; // AZN per m²
export const RESIDENTIAL_EXEMPT_M2 = 30; // first 30 m² of a home is exempt
export const LAND_TAX_RATE_PER_M2 = 0.5; // AZN per m² of residential/garden land (no 30 m² exemption)
// The tax-zones post's URL slug per locale (Azerbaijani slug on az; see
// content/blog/baku-tax-zones/<locale>.md). A plain map rather than the
// blog catalog, which would pull every post into client bundles.
export const TAX_ZONE_BLOG_SLUGS: Record<string, string> = {
  az: "bakida-menzil-satisi-vergisi",
  ru: "baku-tax-zones",
  en: "baku-tax-zones",
};
export const taxZoneBlogSlug = (locale: string) => TAX_ZONE_BLOG_SLUGS[locale] ?? TAX_ZONE_BLOG_SLUGS.az;

// Coefficient → RdYlGn ramp index, mirroring ZONE_COEF_COLOR in
// HeatmapMapMapbox so the calculator's zone dot matches the /map colors
// (greenest = cheapest zone, reddest = most central/expensive).
const COEF_RAMP_INDEX: Record<string, number> = {
  "1.2": 0,
  "1.5": 2,
  "1.8": 4,
  "2.2": 5,
  "3": 7,
  "4": 8,
};

/** Ramp color for a zone coefficient (e.g. the dot in the zone picker). */
export function coefColor(coef: number): string {
  const key = String(coef);
  return HEATMAP_COLORS[COEF_RAMP_INDEX[key] ?? 4];
}

export interface TaxZone {
  /** Display label as printed on the map - "1", "6.3", "10.2", ... */
  zlabel: string;
  /** Zone coefficient, 1.2 (outskirts) → 4.0 (centre). */
  coef: number;
}

// The 18 Baku zones in map order (centre → outskirts), mirroring the
// polygons in public/baku-zones.geojson. Used for the manual picker
// when address auto-detection is unavailable or the user overrides it.
export const TAX_ZONES: TaxZone[] = [
  { zlabel: "1", coef: 4.0 },
  { zlabel: "2", coef: 3.0 },
  { zlabel: "3", coef: 2.2 },
  { zlabel: "4", coef: 2.2 },
  { zlabel: "5", coef: 1.8 },
  { zlabel: "6.1", coef: 1.8 },
  { zlabel: "6.2", coef: 1.8 },
  { zlabel: "6.3", coef: 1.8 },
  { zlabel: "6.4", coef: 1.8 },
  { zlabel: "7", coef: 1.5 },
  { zlabel: "8", coef: 1.5 },
  { zlabel: "9.1", coef: 1.5 },
  { zlabel: "9.2", coef: 1.5 },
  { zlabel: "10.1", coef: 1.2 },
  { zlabel: "10.2", coef: 1.2 },
  { zlabel: "10.3", coef: 1.2 },
  { zlabel: "11", coef: 1.2 },
  { zlabel: "12", coef: 1.2 },
];

// Ray-casting test: is [lng, lat] inside the polygon ring? Ring is an
// array of [lng, lat] vertices (GeoJSON order).
function pointInRing(lng: number, lat: number, ring: number[][]): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = ring[i][0];
    const yi = ring[i][1];
    const xj = ring[j][0];
    const yj = ring[j][1];
    const intersect =
      yi > lat !== yj > lat &&
      lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

interface ZonePolygon {
  zone: TaxZone;
  ring: number[][];
}

let polygonsCache: Promise<ZonePolygon[]> | null = null;

// Loads the zone polygons from the public geojson once and memoises the
// parsed rings. Only "poly" features carrying a coefficient are kept
// (label/line features and the unlabelled artifact polygon are skipped).
function loadZonePolygons(): Promise<ZonePolygon[]> {
  if (!polygonsCache) {
    polygonsCache = fetch("/baku-zones.geojson")
      .then((r) => {
        if (!r.ok) throw new Error(`zones geojson ${r.status}`);
        return r.json();
      })
      .then((fc: GeoJSON.FeatureCollection) => {
        const out: ZonePolygon[] = [];
        for (const f of fc.features ?? []) {
          const p = (f.properties ?? {}) as {
            kind?: string;
            coef?: number | null;
            zlabel?: string | null;
          };
          if (p.kind !== "poly" || p.coef == null || p.zlabel == null) continue;
          const geom = f.geometry;
          if (geom?.type !== "Polygon") continue;
          const ring = geom.coordinates[0] as number[][];
          if (!ring?.length) continue;
          out.push({ zone: { zlabel: p.zlabel, coef: p.coef }, ring });
        }
        return out;
      })
      .catch(() => {
        // Network/parse failure: fall back to manual zone selection.
        polygonsCache = null;
        return [];
      });
  }
  return polygonsCache;
}

// Which tax zone does a coordinate fall into? Returns null when the
// point is outside every zone or the geojson could not be loaded - the
// caller then asks the user to pick a zone manually.
//
// The source polygons are nested, not a clean partition: the centre is
// inside zone 1 AND zone 2 AND zone 3 at once (each broader ring drawn
// under the smaller, more-central one). We return the highest-coefficient
// match - the innermost, most-specific zone - which is also the one
// rendered on top on /map?layer=zones, so the calculator agrees with
// what the user sees and clicks.
export async function findTaxZone(
  lat: number,
  lng: number,
): Promise<TaxZone | null> {
  const polys = await loadZonePolygons();
  let best: TaxZone | null = null;
  for (const { zone, ring } of polys) {
    if (pointInRing(lng, lat, ring) && (!best || zone.coef > best.coef)) {
      best = zone;
    }
  }
  return best;
}

export interface SaleTaxResult {
  /** Area actually taxed, m² (area minus the 30 m² residential exemption). */
  taxableArea: number;
  /** Estimated simplified tax, AZN. */
  tax: number;
  /** True when the 3-year residence exemption zeroes the bill. */
  exemptByResidence: boolean;
}

export function computeSaleTax({
  areaM2,
  coef,
  registered3Years,
}: {
  areaM2: number;
  coef: number;
  registered3Years: boolean;
}): SaleTaxResult {
  if (registered3Years) {
    return { taxableArea: 0, tax: 0, exemptByResidence: true };
  }
  const taxableArea = Math.max(0, areaM2 - RESIDENTIAL_EXEMPT_M2);
  const tax = taxableArea * SALE_TAX_RATE_PER_M2 * coef;
  return { taxableArea, tax, exemptByResidence: false };
}

export interface HouseSaleTaxResult {
  /** House/residential component, AZN (apartment formula on the house area). */
  houseTax: number;
  /** Land component, AZN (land area × 0.5 × coef, doubled if agricultural). */
  landTax: number;
  /** Total house-sale tax, AZN (houseTax + landTax). */
  tax: number;
  /** House area actually taxed, m² (house area minus the 30 m² exemption). */
  houseTaxableArea: number;
  /** True when the 3-year residence exemption zeroes the house component. */
  exemptByResidence: boolean;
}

// A house sale has two summed components:
//   1. House/residential part - identical to the apartment formula
//      (first 30 m² exempt), fully exempt if registered 3+ years.
//   2. Land underneath - landAreaM2 × 0.5 × coef, with NO 30 m² exemption.
//      The 3-year residence exemption does NOT apply to the land.
//      Agricultural-designated land is OUT OF SCOPE: it is taxed as 2x the
//      cadastral land tax (Art 206.1-1, ~0.06 AZN per conditional point,
//      NO zone coefficient), which needs the plot's cadastral score and is
//      not derivable from m² + zone. The UI shows a note for that case
//      instead of a (wrong) number.
export function computeHouseSaleTax({
  houseAreaM2,
  landAreaM2,
  coef,
  registered3Years,
}: {
  houseAreaM2: number;
  landAreaM2: number;
  coef: number;
  registered3Years: boolean;
}): HouseSaleTaxResult {
  const houseTaxableArea = registered3Years
    ? 0
    : Math.max(0, houseAreaM2 - RESIDENTIAL_EXEMPT_M2);
  const houseTax = houseTaxableArea * SALE_TAX_RATE_PER_M2 * coef;

  const landTax = Math.max(0, landAreaM2) * LAND_TAX_RATE_PER_M2 * coef;

  return {
    houseTax,
    landTax,
    tax: houseTax + landTax,
    houseTaxableArea,
    exemptByResidence: registered3Years,
  };
}

// Sale tax for a bare LAND plot (no building). Just the land component:
// 0.5 AZN/m² × zone, no 30 m² exemption and no 3-year residence
// exemption (there's no residence). Agricultural-designated land is the
// exception (2× cadastral land tax, not derivable from m²) - the UI
// shows the same note for it as in house mode.
export function computeLandSaleTax({
  landAreaM2,
  coef,
}: {
  landAreaM2: number;
  coef: number;
}): { tax: number } {
  return { tax: Math.max(0, landAreaM2) * LAND_TAX_RATE_PER_M2 * coef };
}
